import { z } from 'zod';

export const CHILD_AGE_KEYS = ['age1', 'age2', 'age3', 'age4', 'age5'] as const;

const schemaGeneralData = z
    .object({
        agesOfChildren: z.object({
            age1: z.number().int().min(0).optional(),
            age2: z.number().int().min(0).optional(),
            age3: z.number().int().min(0).optional(),
            age4: z.number().int().min(0).optional(),
            age5: z.number().int().min(0).optional(),
        }),
        city: z.string().max(191).optional(),
        dateOfBirth: z.string(),
        email: z.string().max(191).optional(),
        familySituation: z.number().int().min(1).max(6),
        firstName: z.string().max(191),
        gender: z.union([z.literal(1), z.literal(2)]),
        houseNumber: z.string().max(191).optional(),
        lastName: z.string().max(191),
        nationality: z.string().length(2),
        numberOfChildren: z.number().int().min(0).max(5),
        phone1: z.string().max(191).optional(),
        phone2: z.string().max(191).optional(),
        street: z.string().max(191).optional(),
        zipcode: z.string().length(5).optional(),
    })
    .superRefine((data, ctx) => {
        // SKAG requires the age of every declared child; a silent default is not allowed (KC-6267).
        CHILD_AGE_KEYS.slice(0, data.numberOfChildren).forEach((key) => {
            if (data.agesOfChildren[key] === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `agesOfChildren.${key} is required for numberOfChildren = ${data.numberOfChildren}`,
                    path: ['agesOfChildren', key],
                });
            }
        });
    });

const schemaResidenceInfo = z.object({
    rent: z.number().min(0),
    resident_since: z.string().length(10).optional(),
    type: z.number().int().min(1).max(3),
});

const schemaEmployerData = z.object({
    employerCity: z.string().min(1).max(191),
    employerEmail: z.string().max(191).optional(),
    employerHouse: z.string().max(191).optional(),
    employerName: z.string().max(191).optional(),
    employerPhone: z.string().max(191).optional(),
    employerStreet: z.string().max(191).optional(),
    employerZipcode: z.string().length(5),
});

// SKAG profession codes per vendor API docs:
// 1=Arbeiter, 2=Angestellter, 3=Beamter/Pensionär, 4=Rentner,
// 5=Arbeitslos, 6=Hausfrau/Hausmann, 7=Selbstständig, 8=Sonstiges.
// The "employed" buckets (1, 2, 3) carry an Arbeitgeber and therefore require employerData.
const SKAG_PROFESSIONS_REQUIRING_EMPLOYER = new Set([1, 2, 3]);

const schemaAdditionalIncomeData = z.object({
    income: z.string().optional(),
    professionBeginDate: z.string().optional(),
});

const schemaFinancialData = z
    .object({
        additionalIncomeData: schemaAdditionalIncomeData.optional(),
        employerData: schemaEmployerData.optional(),
        employmentRelationshipTerminatedOrLimited: z.boolean().optional(),
        germanTaxClass: z.number().int().min(1).max(5),
        income: z.number(),
        profession: z.number().int().min(1).max(8),
        professionBeginDate: z.string(),
        wageGarnishmentAssignment: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
        if (SKAG_PROFESSIONS_REQUIRING_EMPLOYER.has(data.profession) && !data.employerData) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'employerData is required for Arbeiter (1), Angestellter (2), and Beamter/Pensionär (3)',
                path: ['employerData'],
            });
        }
    });

const schemaBankData = z.object({
    bankName: z.string().optional(),
    iban: z.string().optional(),
});

const schemaSpouse = z.object({
    birthName: z.string().optional(),
    dateBirth: z.string().optional(),
    firstName: z.string().optional(),
    income: z.string().optional(),
});

export const schemaCreditApplicationRequest = z.object({
    bankData: schemaBankData.optional(),
    financialData: schemaFinancialData,
    generalData: schemaGeneralData,
    residenceInfo: schemaResidenceInfo,
    spouse: schemaSpouse.optional(),
});

export type SchemaCreditApplicationRequest = z.infer<typeof schemaCreditApplicationRequest>;
